import { Button, IconButton, List, ListItem, ListSubheader, Typography } from '@mui/material'
import { GroupAdd, GroupRemove, VolumeOff, VolumeUp } from '@mui/icons-material'
import { useFragment, useLazyLoadQuery } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { memo, useCallback, useState } from 'react'
import { ChatThreadUsers_Query } from '__generated__/ChatThreadUsers_Query.graphql'
import { ChatThreadUsers_Thread$key } from '__generated__/ChatThreadUsers_Thread.graphql'
import Condition from 'components/Condition'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { useSnackbar } from 'hooks/redux'
import { ChatThreadUsers_JoinChatThreadMutation } from '__generated__/ChatThreadUsers_JoinChatThreadMutation.graphql'
import { ChatThreadUsers_LeaveChatThreadMutation } from '__generated__/ChatThreadUsers_LeaveChatThreadMutation.graphql'
import ConditionSwitch from 'components/ConditionSwitch'
import { ChatThreadUsers_MuteChatThreadMutation } from '__generated__/ChatThreadUsers_MuteChatThreadMutation.graphql'
import Contact from 'components/Contact'
import ChatThreadEditDialog from 'components/ChatThreadEditDialog'

export const ChatThreadUsersQuery = graphql`
  query ChatThreadUsers_Query($threadId: ID!) {
    thread: node(id: $threadId) {
      ...ChatThreadUsers_Thread
    }
  }
`

export interface IChatThreadUsersProps {
  threadId: string
  showJoin: boolean
  showLeave: boolean
  showAddReviewers: boolean
}

function ChatThreadUsers(props: IChatThreadUsersProps) {
  const { threadId, showAddReviewers, showLeave, showJoin } = props
  const data = useLazyLoadQuery<ChatThreadUsers_Query>(ChatThreadUsersQuery, { threadId }, { fetchPolicy: 'store-and-network' })
  const [contactsOpen, setContactsOpen] = useState(false)
  const thread = useFragment(graphql`
    fragment ChatThreadUsers_Thread on ChatThread {
      id
      joined
      threadType
      purpose
      muted
      recipient{
        ... on User {
        id
        }

        ... on ChatBot {
          id
        }

        ...ChatThreadEditDialog_Recipient
      }

      users {
        id
        ...Contact_Contact
      }

      ...ChatThreadEditDialog_Thread
    }
  `, data?.thread as ChatThreadUsers_Thread$key)

  const { showSnackbarMessage } = useSnackbar()

  const joinThreadMutation = useQuickCommit<ChatThreadUsers_JoinChatThreadMutation>(graphql`
    mutation ChatThreadUsers_JoinChatThreadMutation($input: JoinChatThreadInput!) {
      joinChatThread(input: $input) {
        chatThread {
          joined # will change to true
          threadType # joining will make this a group chat if it's not already

          # The following fields might change
          defaultTitle
          avatarUrl

          ...ChatThreadUsers_Thread
        }
      }
    }
  `)

  const leaveThreadMutation = useQuickCommit<ChatThreadUsers_LeaveChatThreadMutation>(graphql`
    mutation ChatThreadUsers_LeaveChatThreadMutation($input: LeaveChatThreadInput!) {
      leaveChatThread(input: $input) {
        chatThread {
          joined # will change to false
          threadType # leaving might make this a direct chat
          
          # The following fields might change if this goes back to a direct chat
          defaultTitle
          avatarUrl

          ...ChatThreadUsers_Thread
        }
      }
    }
  `)

  const MuteChatThreadMutation = useQuickCommit<ChatThreadUsers_MuteChatThreadMutation>(graphql`
    mutation ChatThreadUsers_MuteChatThreadMutation($input: MuteChatThreadInput!) {
      muteChatThread(input: $input) {
        chatThread {
          muted
          id
        }
      }
    }
    `)

  const joinThread = useCallback(async () => {
    const result = await joinThreadMutation.execute({
      input: {
        chatThreadId: thread.id,
      },
    })
    if (result?.joinChatThread?.chatThread?.joined) {
      showSnackbarMessage('You have joined this chat and will receive notifications for new messages', { level: 'success' })
    }
  }, [thread?.id, joinThreadMutation, showSnackbarMessage])

  const leaveThread = useCallback(async () => {
    const result = await leaveThreadMutation.execute({
      input: {
        chatThreadId: thread.id,
      },
    })
    if (result?.leaveChatThread && !result?.leaveChatThread?.chatThread?.joined) {
      showSnackbarMessage('You have left this chat and will not receive notifications for new messages', { level: 'success' })
    }
  }, [thread?.id, joinThreadMutation, showSnackbarMessage])

  const muteThread = useCallback(async () => {
    const result = await MuteChatThreadMutation.execute({
      input: {
        chatThreadId: thread.id,
        muted: !thread?.muted || false,
      },
    })

    if (result?.muteChatThread?.chatThread?.id) {
      if (result?.muteChatThread?.chatThread?.muted) {
        showSnackbarMessage('You have muted this chat and will not receive notifications for new messages', { level: 'success' })
      } else {
        showSnackbarMessage('You have unmuted this chat and will receive notifications for new messages', { level: 'success' })
      }
    }
  }, [thread?.id, MuteChatThreadMutation, showSnackbarMessage])

  const muteText = thread?.muted ? 'Unmute' : 'Mute'

  if (!thread) return null

  return (
    <List>
      <ListSubheader disableSticky>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <ConditionSwitch>
            <Condition condition={thread?.purpose === 'applicant_feedback'}>
              Reviewers
            </Condition>
            <Condition condition>
              Participants
            </Condition>
          </ConditionSwitch>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body1">{muteText}</Typography>
            <IconButton onClick={muteThread} disabled={MuteChatThreadMutation.loading}>
              {thread.muted ? <VolumeOff /> : <VolumeUp />}
            </IconButton>
          </div>
        </div>
      </ListSubheader>

      {contactsOpen && (
        <ChatThreadEditDialog
          threadFragmentRef={thread}
          recipientFragmentRef={thread?.recipient || undefined}
          onClose={() => setContactsOpen(false)}
        />
      )}

      {thread.users.map(contact => (
        <Contact
          key={contact.id}
          contactFragmentRef={contact}
          hideRelationship
        />
      ))}

      <Condition condition={Boolean(thread.joined && (thread.threadType === 'group' || thread.purpose) && showLeave)}>
        <ListItem>
          <Button onClick={leaveThread} style={{ width: '100%' }} disabled={leaveThreadMutation.loading} startIcon={<GroupRemove />}>
            Leave
          </Button>
        </ListItem>
      </Condition>

      <Condition condition={!thread.joined && showJoin}>
        <ListItem>
          <Button onClick={joinThread} style={{ width: '100%' }} disabled={joinThreadMutation.loading} startIcon={<GroupAdd />}>
            Join
          </Button>
        </ListItem>
      </Condition>

      <Condition condition={showAddReviewers}>
        <ListItem>
          <Button onClick={() => setContactsOpen(true)} style={{ width: '100%' }} disabled={joinThreadMutation.loading} startIcon={<GroupAdd />}>
            Add Reviewers
          </Button>
        </ListItem>
      </Condition>
    </List>
  )
}

export default memo(ChatThreadUsers)
